import { Translate } from "@omni/types/utils/translate";
import { AxiosResponse } from "axios";
import { addMethod, StringSchema, string as yupString } from "yup";

import { create } from "@/api/api";
import { update as updateVatInfo } from "@/redux/modules/value/vatInfo/vatInfo.module";
import { getVatInfoFor } from "@/redux/modules/value/vatInfo/vatInfo.selectors";
import {
    doVatCheckBe,
    doVatCheckDe,
    doVatCheckFr,
    doVatCheckLu,
    doVatCheckNl,
    doVatCheckOther
} from "@/services/vatCalculation.service";
import { Config } from "@/utils/config.utils";
import { getErrorMessage } from "@/utils/errors";

import { string } from "./general.RHF";

type VATValidationResponse = {
    countryCode?: string;
    city?: string;
    zipcode?: string;
    street?: string;
    number?: string;
    box?: string | null;
    companyName?: string;
    valid: boolean;
    vatLiable: boolean;
};

const VATValidationAPI = create("", true);
addMethod<StringSchema>(
    yupString,
    "vatCalculationRHF",
    function (required: boolean) {
        return this.test("vatCalculationRHF", {
            exclusive: true
        }, function (value) {
            const { createError, parent } = this;
            if (!required && !value) return true;
            if (required && !value) {
                return createError({
                    message: () => () => (t: Translate) =>
                        t("general.form.input.validation.required")
                });
            }

            const vatNrPrefix = parent[`vatNrPrefix`];

            const parsedValue = value?.replaceAll("_", "");

            return new Promise((resolve, reject) => {
                let rValue;
                if (parsedValue && vatNrPrefix) {
                    switch (vatNrPrefix.toLowerCase()) {
                        case "":
                        case null:
                            rValue = false;
                            break;
                        case "be":
                            rValue = doVatCheckBe(parsedValue);
                            break;
                        case "nl":
                            rValue = doVatCheckNl(parsedValue);
                            break;
                        case "fr":
                            rValue = doVatCheckFr(parsedValue);
                            break;
                        case "de":
                            rValue = doVatCheckDe(parsedValue);
                            break;
                        case "lu":
                            rValue = doVatCheckLu(parsedValue);
                            break;
                        default:
                            rValue = doVatCheckOther(parsedValue);
                    }
                }

                if (rValue) {
                    const store = window.storeManager;
                    const vatInfo = getVatInfoFor(value!)(store.getState());

                    if (
                        vatInfo &&
                        vatInfo.vatNr === parsedValue &&
                        vatNrPrefix === vatInfo.vatNrPrefix
                    ) {
                        return resolve(true);
                    }

                    store.dispatch(updateVatInfo({ isLoading: true }));

                    VATValidationAPI.request<
                        any,
                        AxiosResponse<VATValidationResponse>,
                        AxiosResponse<VATValidationResponse>
                    >({
                        method: "get",
                        url: `${Config.general["api-base-url"]}/i18nservice/${Config.general["basestore"]}/vat/${vatNrPrefix}/${parsedValue}`,
                        data: {} as any,
                        headers: {
                            cache: "force-cache",
                            timeout: 5000,
                            mode: "cors"
                        }
                    })
                        .then((response) => {
                            if (response?.data?.valid) {
                                // dispatch to store
                                const vatInfo = {
                                    vatNrPrefix,
                                    vatNr: value,
                                    ...(response?.data || {})
                                };
                                store.dispatch(
                                    updateVatInfo({
                                        data: { ...vatInfo }
                                    })
                                );
                                resolve(true);
                            } else {
                                reject(
                                    createError({
                                        message: () => () => (t: Translate) =>
                                            t(
                                                "general.form.input.validation.vat.invalid"
                                            )
                                    })
                                );
                            }
                        })
                        .catch((error: any) => {
                            const data =
                                error.response &&
                                typeof error.response.data === "object"
                                    ? error.response.data?.message
                                    : error.response?.data;
                            if (data) {
                                reject(
                                    createError({
                                        message: () => () => (t: Translate) =>
                                            getErrorMessage(
                                                t,
                                                `apiMessages.title.${data}`
                                            )
                                    })
                                );
                            }

                            resolve(true);
                        })
                        .finally(() => {
                            store.dispatch(
                                updateVatInfo({
                                    isLoading: false
                                })
                            );
                        });
                } else {
                    reject(
                        createError({
                            message: () => () => (t: Translate) =>
                                t("general.form.input.validation.vat.invalid")
                        })
                    );
                }
            });
        });
    }
);

export const vatNr = string()
    .nullable()
    .required()
    .when("vatNrPrefix", {
        is: (val: string) =>
            ["BE", "DE", "LU", "FR", "NL"].some(
                (v) => v.toLowerCase() === val.toLowerCase()
            ),
        then: (schema) => schema.vatCalculationRHF(true),
        otherwise: (schema) => schema.min(8).max(12).vatCalculationRHF(true)
    });

export const optionalVatNr = string()
    .nullable()
    .when("vatNrPrefix", {
        is: (val: string) =>
            ["BE", "DE", "LU", "FR", "NL"].some(
                (v) => v.toLowerCase() === val.toLowerCase()
            ),
        then: (schema) => schema.vatCalculationRHF(false),
        otherwise: (schema) => schema.min(8).max(12).vatCalculationRHF(false)
    });

export const vatNrPrefix = string().nullable().required();

export const optionalVatNrPrefix = string().nullable();
